import React, { useState, useEffect } from "react";
import { auth } from "../firebase";

const API_BASE =
  "https://sellerproxy-898712575285.us-central1.run.app";

/* ==========================================================
   GalleryCategorySuggest
   ----------------------------------------------------------
   Contributor-facing replacement for GalleryCategoryCreator.
   Contributors can NOT create galleries/categories/subcategories
   directly (that requires admin access on the backend). Instead
   they submit a suggestion which shows up in GalleryAdmin's
   "Suggestions" tab for an admin to approve or reject.

   Backend endpoints expected (new):
     POST /api/gallery/category-suggestions        (contributor, auth required)
     GET  /api/gallery/category-suggestions/mine    (contributor, auth required)
========================================================== */

const TYPES = [
  { id: "gallery", label: "New Gallery" },
  { id: "category", label: "New Category" },
  { id: "subcategory", label: "New Subcategory" }
];

export default function GalleryCategorySuggest({ user, onSubmitted }) {
  // ── reference data (existing galleries/categories to attach to) ──
  const [galleries, setGalleries] = useState([]);
  const [categories, setCategories] = useState([]);

  // ── form state ─────────────────────────────────────────────
  const [type, setType] = useState("gallery");
  const [name, setName] = useState("");
  const [selectedGalleryId, setSelectedGalleryId] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ── my suggestions list ────────────────────────────────────
  const [mySuggestions, setMySuggestions] = useState([]);
  const [loadingMine, setLoadingMine] = useState(false);

  async function getHeaders() {
    const u = auth.currentUser;
    if (!u) throw new Error("Please sign in first");
    const token = await u.getIdToken(true);
    return {
      "X-Firebase-Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    };
  }

  async function loadGalleries() {
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_BASE}/api/gallery/galleries`, { headers });
      const data = await res.json();
      setGalleries(Array.isArray(data?.galleries) ? data.galleries : []);
    } catch (err) {
      console.error("Load galleries error:", err);
    }
  }

  async function loadCategories(galleryId) {
    if (!galleryId) {
      setCategories([]);
      return;
    }
    try {
      const headers = await getHeaders();
      const res = await fetch(
        `${API_BASE}/api/gallery/categories/${galleryId}`,
        { headers }
      );
      const data = await res.json();
      setCategories(Array.isArray(data?.categories) ? data.categories : []);
    } catch (err) {
      console.error("Load categories error:", err);
    }
  }

  async function loadMySuggestions() {
    try {
      setLoadingMine(true);
      const headers = await getHeaders();
      const res = await fetch(
        `${API_BASE}/api/gallery/category-suggestions/mine`,
        { headers }
      );
      if (!res.ok) throw new Error("Failed to load suggestions");
      const data = await res.json();
      setMySuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
    } catch (err) {
      // Non-fatal: form still works even if history can't be loaded.
      console.error("Load my suggestions error:", err);
    } finally {
      setLoadingMine(false);
    }
  }

  useEffect(() => {
    if (!user?.uid) return;
    loadGalleries();
    loadMySuggestions();
  }, [user]);

  useEffect(() => {
    loadCategories(selectedGalleryId);
    setSelectedCategoryId("");
  }, [selectedGalleryId]);

  // reset dependent fields when switching suggestion type
  useEffect(() => {
    setName("");
    if (type === "gallery") {
      setSelectedGalleryId("");
      setSelectedCategoryId("");
    }
    if (type === "category") {
      setSelectedCategoryId("");
    }
  }, [type]);

  async function submitSuggestion() {
    if (!name.trim()) {
      alert("Please enter a name for your suggestion");
      return;
    }
    if (type === "category" && !selectedGalleryId) {
      alert("Please select the gallery this category belongs under");
      return;
    }
    if (type === "subcategory" && (!selectedGalleryId || !selectedCategoryId)) {
      alert("Please select the gallery and category this subcategory belongs under");
      return;
    }

    try {
      setSubmitting(true);
      const headers = await getHeaders();
      const gallery = galleries.find((g) => g.id === selectedGalleryId);
      const category = categories.find((c) => c.id === selectedCategoryId);

      const res = await fetch(`${API_BASE}/api/gallery/category-suggestions`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          type,
          name: name.trim(),
          galleryId: selectedGalleryId || "",
          galleryName: gallery?.name || "",
          categoryId: selectedCategoryId || "",
          categoryName: category?.name || "",
          note: note.trim(),
          uid: user?.uid || "",
          userName: user?.displayName || "",
          userEmail: user?.email || ""
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || data?.error || "Failed to submit suggestion");
      }

      setName("");
      setNote("");
      await loadMySuggestions();
      if (onSubmitted) onSubmitted();
      alert("Suggestion submitted. An admin will review it shortly.");
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to submit suggestion");
    } finally {
      setSubmitting(false);
    }
  }

  // ── styles (mirrors GalleryCategoryCreator styling) ─────────
  const card = {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 20,
    marginBottom: 20
  };

  const inputStyle = {
    width: "100%",
    padding: 12,
    borderRadius: 10,
    border: "1px solid #d1d5db",
    marginBottom: 10,
    fontSize: 14,
    boxSizing: "border-box"
  };

  const selectStyle = { ...inputStyle, background: "#fff", cursor: "pointer" };

  const typeBtn = (active) => ({
    border: active ? "none" : "1px solid #d1d5db",
    padding: "10px 16px",
    borderRadius: 10,
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
    color: active ? "#fff" : "#374151",
    background: active
      ? "linear-gradient(135deg,#2563eb,#7c3aed)"
      : "#fff"
  });

  const submitBtnStyle = {
    border: "none",
    padding: "11px 20px",
    borderRadius: 10,
    color: "#fff",
    fontWeight: 700,
    fontSize: 14,
    cursor: submitting ? "not-allowed" : "pointer",
    opacity: submitting ? 0.7 : 1,
    background: "linear-gradient(135deg,#2563eb,#7c3aed)"
  };

  function statusPill(status) {
    const s = String(status || "pending").toLowerCase();
    const styleMap = {
      approved: { background: "#dcfce7", color: "#166534" },
      rejected: { background: "#fee2e2", color: "#991b1b" },
      pending: { background: "#fef3c7", color: "#92400e" }
    };
    const style = styleMap[s] || styleMap.pending;
    return (
      <span
        style={{
          ...style,
          padding: "4px 10px",
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 700,
          textTransform: "capitalize"
        }}
      >
        {s}
      </span>
    );
  }

  return (
    <div>
      <h3 style={{ marginTop: 0, marginBottom: 6 }}>
        Suggest a New Category
      </h3>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0, marginBottom: 20 }}>
        Don't see the right gallery, category or subcategory for your content?
        Submit a suggestion below and a PITICS admin will review it. Once approved,
        it will appear as an option when you upload.
      </p>

      <div style={card}>
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          {TYPES.map((t) => (
            <button
              key={t.id}
              type="button"
              style={typeBtn(type === t.id)}
              onClick={() => setType(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {(type === "category" || type === "subcategory") && (
          <>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
              Gallery
            </label>
            <select
              value={selectedGalleryId}
              onChange={(e) => setSelectedGalleryId(e.target.value)}
              style={selectStyle}
            >
              <option value="">Select existing gallery...</option>
              {galleries.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </>
        )}

        {type === "subcategory" && (
          <>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
              Category
            </label>
            <select
              value={selectedCategoryId}
              onChange={(e) => setSelectedCategoryId(e.target.value)}
              disabled={!selectedGalleryId}
              style={{ ...selectStyle, opacity: !selectedGalleryId ? 0.5 : 1 }}
            >
              <option value="">Select existing category...</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </>
        )}

        <label style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
          {type === "gallery" && "New gallery name"}
          {type === "category" && "New category name"}
          {type === "subcategory" && "New subcategory name"}
        </label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter a name"
          style={inputStyle}
        />

        <label style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
          Note for the admin (optional)
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Why is this needed? What kind of content would go here?"
          rows={3}
          style={{ ...inputStyle, resize: "vertical" }}
        />

        <button
          type="button"
          onClick={submitSuggestion}
          disabled={submitting}
          style={submitBtnStyle}
        >
          {submitting ? "Submitting..." : "Submit Suggestion"}
        </button>
      </div>

      <div style={card}>
        <h4 style={{ marginTop: 0 }}>Your Suggestions</h4>

        {loadingMine && (
          <p style={{ color: "#6b7280", fontSize: 13 }}>Loading...</p>
        )}

        {!loadingMine && mySuggestions.length === 0 && (
          <p style={{ color: "#6b7280", fontSize: 13 }}>
            You haven't submitted any suggestions yet.
          </p>
        )}

        {mySuggestions.map((s) => (
          <div
            key={s.id}
            style={{
              padding: 14,
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              marginBottom: 10
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap"
              }}
            >
              <div>
                <strong>{s.name}</strong>{" "}
                <span style={{ color: "#9ca3af", fontSize: 12 }}>
                  ({s.type})
                </span>
              </div>
              {statusPill(s.status)}
            </div>

            {(s.galleryName || s.categoryName) && (
              <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
                {s.galleryName && <>Gallery: {s.galleryName} </>}
                {s.categoryName && <>· Category: {s.categoryName}</>}
              </div>
            )}

            {s.note && (
              <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
                Note: {s.note}
              </div>
            )}

            {s.status === "rejected" && s.reason && (
              <div style={{ color: "#991b1b", fontSize: 12, marginTop: 6 }}>
                Reason: {s.reason}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
